// @ts-nocheck
import { useEffect, useState, useMemo, memo, type MouseEventHandler } from 'react';
import { FaThumbsUp } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import useProductEvents from '@core/hooks/cohesion/useProductEvents';
import {
  selectRecommenderPopupListId,
  setHeclidMapEntry,
  setProductCorrelationIdMapEntry,
  setViewCorrelationIdMapEntry,
} from '@core/reducers/eventingSlice';
import {
  setLeadDelivery,
  selectLeadDelivery,
  setCurrentProgram,
  selectSubmissions,
  setShowDuplicatePopup,
} from '@core/reducers/matchesSlice';
import { type VoyagerResult } from '@core/ts/results';

import styles from './StickyBanner.module.css';

interface StickyBannerProps {
  result: VoyagerResult;
  eventingOverrides?: any;
  index?: number;
  position?: number;
}

const StickyBanner = ({ result, eventingOverrides, position, index }: StickyBannerProps) => {
  const dispatch = useDispatch();
  const popupListId = useSelector(selectRecommenderPopupListId);
  const newViewCorrelationId = useMemo(() => uuid(), []);
  const isSingleSubmissionRecommender = true;

  const { productLoaded, productViewed, productClicked } = useProductEvents({
    listId: popupListId,
    viewCorrelationId: newViewCorrelationId,
    product: {
      sku: String(result?.cap?.id),
      variant: result?.program?.degree?.slug,
      productId: String(result?.program?.id),
      name: result?.program?.subject?.slug,
      category: result?.program?.category?.slug,
      brand: result?.school?.slug,
      position,
      location: eventingOverrides?.location,
      formatType: isSingleSubmissionRecommender ? 'app' : 'multi-program',
      formatSubtype: 'voyager-recommender-banner',
      positionEngine: 'algolia',
      ...eventingOverrides?.product,
    },
    customDimensions: eventingOverrides?.customDimensions,
  });
  const submissions = useSelector(selectSubmissions);
  const handleProductClick = () => {
    const isSchoolSubmitted = !!submissions?.find((submission) => submission.school.id === result?.school.id);
    // If school is already submitted, show duplicate popup
    if (isSchoolSubmitted) {
      dispatch(
        setLeadDelivery({
          show: false,
          currentPrograms: [result],
          showExitStep: false,
        })
      );
      dispatch(setShowDuplicatePopup(true));
      return;
    }

    // If program hasn't been submitted, show lead delivery slide-out
    const productCorrelationId = uuid();
    dispatch(setProductCorrelationIdMapEntry({ key: result.program.id, value: productCorrelationId }));
    productClicked({ correlationId: productCorrelationId });

    dispatch(
      setLeadDelivery({
        show: true,
        currentPrograms: [result],
        showExitStep: false,
      })
    );
  };
  return (
    <div className={styles.stickyBanner}>
      <div className={styles.bannerContent}>
        <div className={styles.bannerContentLeft}>
          <div className={styles.logoContainer}>
            <img
              src={
                result?.school?.slug
                  ? `https://simple-storage-server.highereducation.com/${result?.school.slug}.png?w=130&h=80`
                  : undefined
              }
              alt={result?.school?.name}
            />
          </div>
          <div className={styles.title}>{result?.school?.name}</div>
        </div>
        <div className={styles.bannerContentRight}>
          <div className={styles.bestMatchWrapper}>
            <span className={styles.thumbsIconContainer}>
              <FaThumbsUp size={11} color="var(--tertiary-800)" />
            </span>
            <span className={styles.label}>Best Match</span>
          </div>
          <div className={styles.title}>{result?.program?.name}</div>
          <button
            onClick={handleProductClick}
            data-testid="recommender-popup-card-button-banner"
            type="button"
            className={styles.cardBtnCta}
          >
            Explore Program
          </button>
        </div>
      </div>
    </div>
  );
};
export default StickyBanner;
